import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Button, 
  Grid, 
  Paper,
  Slider,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { toast } from 'react-toastify';
import Order from '../../models/order';
import Invoice from '../../models/invoice';
import BankTransaction from '../../models/bankTransaction';

const OrderBankTransactionMatch = ({ mode = 'order' }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isInvoice = mode === 'invoice';
  const docPath = isInvoice ? 'invoices' : 'orders';
  const docWord = isInvoice ? 'hóa đơn' : 'đơn hàng';
  const [order, setOrder] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapping, setMapping] = useState(false);
  const [matchThreshold, setMatchThreshold] = useState(0.7);
  const [daysRange, setDaysRange] = useState(14);
  const [extendedRange, setExtendedRange] = useState(false);

  // Create state to track current and applied filter values
  const [appliedThreshold, setAppliedThreshold] = useState(matchThreshold);
  const [appliedDaysRange, setAppliedDaysRange] = useState(daysRange);
  const [applyingFilters, setApplyingFilters] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Fetch order details and potential matches
  const fetchOrderAndMatches = async (threshold, days, isInitialLoad = false) => {
    if (isInitialLoad) {
      setLoading(true);
    }
    try {
      // Fetch the document (order or invoice); map invoice into order-shape so the
      // rest of the JSX (order.name / order.total_price / order.user) works as-is.
      if (isInvoice) {
        const inv = await new Invoice().getById(id);
        setOrder(inv ? {
          id: inv.id, name: inv.code, total_price: inv.total,
          created_at: inv.created_at, financial_status: inv.status, status: inv.status,
          user: inv.user,
        } : null);
      } else {
        const orderModel = new Order();
        await orderModel.getOrderById(id);
        setOrder(orderModel);
      }

      const bankTransaction = new BankTransaction();
      const response = isInvoice
        ? await bankTransaction.getMatchesForInvoice(id, threshold, days)
        : await bankTransaction.getMatchesForOrder(id, threshold, days);

      console.log(response.data);

      if (response.success && response.data) {
        setMatches(response.data.data);
        console.log("Matches:", response.data.data);
        console.log("Matches length:", response.data.data.length);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Đã xảy ra lỗi khi tải dữ liệu", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
      setApplyingFilters(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchOrderAndMatches(appliedThreshold, appliedDaysRange, true);
  }, [id]);

  // Apply filter values and trigger reload
  const handleApplyFilters = () => {
    setApplyingFilters(true);
    setAppliedThreshold(matchThreshold);
    setAppliedDaysRange(daysRange);
    fetchOrderAndMatches(matchThreshold, daysRange);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' })
      .format(amount)
      .replace("₫", "");
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('vi-VN', options);
  };

  // Open confirmation dialog before mapping
  const handleOpenConfirmDialog = (transaction) => {
    setSelectedTransaction(transaction);
    setConfirmDialogOpen(true);
  };

  // Close confirmation dialog
  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setSelectedTransaction(null);
  };

  // Handle mapping between transaction and order
  const handleMapTransaction = async () => {
    if (!selectedTransaction) return;
    
    setMapping(true);
    setConfirmDialogOpen(false);
    
    try {
      let result;
      if (isInvoice) {
        const r = await new Invoice().matchTransaction(id, selectedTransaction.id);
        result = { success: !!r, message: r ? 'Ghép giao dịch thành công' : 'Ghép giao dịch thất bại' };
      } else {
        const bankTransaction = new BankTransaction();
        result = await bankTransaction.mapToOrder(selectedTransaction.id, parseInt(id));
      }

      if (result.success) {
        toast.success(result.message || "Ghép giao dịch thành công", {
          position: "top-center",
          autoClose: 3000,
        });
        navigate(`/${docPath}/${id}`);
      } else {
        toast.error(result.message || "Ghép giao dịch thất bại", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error mapping transaction:", error);
      toast.error("Đã xảy ra lỗi khi ghép giao dịch", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setMapping(false);
      setSelectedTransaction(null);
    }
  };

  // Handle going back to order detail
  const handleBack = () => {
    navigate(`/${docPath}/${id}`);
  };

  // Handle threshold change
  const handleThresholdChange = (event, newValue) => {
    setMatchThreshold(newValue);
  };

  // Handle days range change
  const handleDaysRangeChange = (event, newValue) => {
    setDaysRange(newValue);
  };

  // Handle extended range checkbox change
  const handleExtendedRangeChange = (event) => {
    const checked = event.target.checked;
    setExtendedRange(checked);
    // Reset to 30 if unchecking and current value > 30
    if (!checked && daysRange > 30) {
      setDaysRange(30);
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!order) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">Không tìm thấy thông tin {docWord}</Alert>
        <Button 
          variant="contained" 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          Quay lại
        </Button>
      </Container>
    );
  }

return (
    <Container sx={{ mt: 3, mb: 5 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          Quay lại
        </Button>
        <Typography variant="h5" component="h1">
          Ghép {docWord} #{order.name} với giao dịch ngân hàng
        </Typography>
      </Box>

      {/* Order information card */}
      <Card sx={{ mb: 4, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 2 }}>
          <Typography variant="h6">Thông tin {docWord}</Typography>
        </Box>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'primary.light', mr: 1, width: 28, height: 28 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Typography variant="subtitle2" color="text.secondary">Mã đơn hàng:</Typography>
              </Box>
              <Typography variant="body1" fontWeight="medium" sx={{ ml: 4.5 }}>{order.name}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'primary.light', mr: 1, width: 28, height: 28 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Typography variant="subtitle2" color="text.secondary">Khách hàng:</Typography>
              </Box>
              <Typography variant="body1" fontWeight="medium" sx={{ ml: 4.5 }}>{order.user?.name || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'primary.light', mr: 1, width: 28, height: 28 }}>
                  <CalendarTodayIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Typography variant="subtitle2" color="text.secondary">Ngày tạo:</Typography>
              </Box>
              <Typography variant="body1" fontWeight="medium" sx={{ ml: 4.5 }}>{formatDate(order.created_at)}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'success.light', mr: 1, width: 28, height: 28 }}>
                  <AttachMoneyIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Typography variant="subtitle2" color="text.secondary">Tổng tiền:</Typography>
              </Box>
              <Typography variant="body1" fontWeight="bold" color="success.main" sx={{ ml: 4.5 }}>
                {formatCurrency(order.total_price)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'primary.light', mr: 1, width: 28, height: 28 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Typography variant="subtitle2" color="text.secondary">Trạng thái thanh toán:</Typography>
              </Box>
              <Box sx={{ ml: 4.5 }}>
                <Chip 
                  label={order.financial_status === 'PAID' ? 'Đã thanh toán' : 
                    order.financial_status === 'MAPPED_BANK_TRANSACTION' ? 'Đã ghép GD ngân hàng' : 
                      'Chưa thanh toán'} 
                  color={order.financial_status === 'PAID' || order.financial_status === 'MAPPED_BANK_TRANSACTION' ? 'success' : 'error'}
                  size="small"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'primary.light', mr: 1, width: 28, height: 28 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Typography variant="subtitle2" color="text.secondary">Trạng thái đơn hàng:</Typography>
              </Box>
              <Box sx={{ ml: 4.5 }}>
                <Chip 
                  label={order.status} 
                  color={order.status === 'CANCELLED' ? 'error' : 'primary'}
                  size="small"
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Match parameters card */}
      <Card sx={{ mb: 4, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: 'info.main', color: 'white', p: 2 }}>
          <Typography variant="h6">Điều chỉnh tham số tìm kiếm</Typography>
        </Box>
        <CardContent>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SearchIcon color="info" sx={{ mr: 1 }} />
                <Typography gutterBottom fontWeight="medium">
                  Ngưỡng điểm trùng khớp: {matchThreshold}
                </Typography>
              </Box>
              <Slider
                value={matchThreshold}
                onChange={handleThresholdChange}
                min={0}
                max={1}
                step={0.05}
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.5, label: '0.5' },
                  { value: 1, label: '1' }
                ]}
                valueLabelDisplay="auto"
                sx={{ color: 'info.main' }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Điểm cao hơn = kết quả chính xác hơn, nhưng ít kết quả hơn
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CalendarTodayIcon color="info" sx={{ mr: 1 }} />
                <Typography gutterBottom fontWeight="medium">
                  Khoảng thời gian (ngày): {daysRange}
                </Typography>
              </Box>
              <Slider
                value={daysRange}
                onChange={handleDaysRangeChange}
                min={1}
                max={extendedRange ? 180 : 30}
                step={1}
                marks={extendedRange ? [
                  { value: 1, label: '1' },
                  { value: 30, label: '30' },
                  { value: 90, label: '90' },
                  { value: 180, label: '180' }
                ] : [
                  { value: 1, label: '1' },
                  { value: 14, label: '14' },
                  { value: 30, label: '30' }
                ]}
                valueLabelDisplay="auto"
                sx={{ color: 'info.main' }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Tìm giao dịch trong bao nhiêu ngày trước/sau ngày đặt hàng
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={extendedRange}
                    onChange={handleExtendedRangeChange}
                    color="info"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Tìm kiếm đến 180 ngày
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                color="info"
                onClick={handleApplyFilters}
                disabled={applyingFilters}
                startIcon={<SearchIcon />}
                sx={{ 
                  minWidth: 160,
                  py: 1,
                  fontWeight: 'medium'
                }}
              >
                {applyingFilters ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Đang tìm kiếm...
                  </>
                ) : 'Áp dụng tìm kiếm'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {matches.length === 0 ? (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 4,
            p: 2,
            '& .MuiAlert-message': { 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
            Không tìm thấy giao dịch ngân hàng phù hợp với đơn hàng này
          </Typography>
          <Typography variant="body2">
            Hãy điều chỉnh ngưỡng điểm trùng khớp hoặc tăng khoảng thời gian tìm kiếm để xem thêm kết quả
          </Typography>
        </Alert>
      ) : (
        <>
          {/* Potential matches section */}
          {matches.map((match, index) => (
            <Card key={index} sx={{ mb: 3, overflow: 'hidden', boxShadow: index === 0 ? 3 : 1 }}>
              {/* Transaction Header */}
              <Box sx={{ 
                p: 2, 
                bgcolor: match.score > 0.9 ? '#e8f5e9' : match.score > 0.7 ? '#e3f2fd' : '#fff8e1',
                borderBottom: '1px solid',
                borderColor: match.score > 0.9 ? '#a5d6a7' : match.score > 0.7 ? '#90caf9' : '#ffe082',
              }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" fontWeight="bold">
                      {match.transaction.code}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Chip
                        label={match.transaction.status === 'PROCESSED' ? 'Đã xử lý' : 
                              match.transaction.status === 'PENDING' ? 'Đang chờ' : 'Từ chối'}
                        color={match.transaction.status === 'PROCESSED' ? 'success' : 
                              match.transaction.status === 'PENDING' ? 'warning' : 'error'}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {match.transaction.transaction_type || 'Không xác định'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                    <AttachMoneyIcon 
                      sx={{ 
                        fontSize: 28, 
                        color: match.transaction.amount === order.total_price ? 'success.main' : 'text.secondary',
                        mr: 1
                      }} 
                    />
                    <Typography 
                      variant="h6" 
                      fontWeight="bold"
                      color={match.transaction.amount === order.total_price ? 'success.main' : 'text.primary'}
                    >
                      {formatCurrency(match.transaction.amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {formatDate(match.transaction.happened_at)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Transaction Details */}
              <CardContent>
                <Grid container spacing={3}>
                  {/* Left Column - Transaction Details */}
                  <Grid item xs={12} md={7}>
                    {/* Description */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        <DescriptionIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
                        Mô tả:
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f9f9f9' }}>
                        <Typography variant="body2">{match.transaction.description}</Typography>
                      </Paper>
                    </Box>

                    {/* Match Score with Progress Bar */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          <AssignmentTurnedInIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
                          Mức độ trùng khớp:
                        </Typography>
                        <Typography 
                          variant="body2" 
                          fontWeight="bold" 
                          color={
                            match.score > 0.9 ? 'success.main' : 
                            match.score > 0.7 ? 'primary.main' : 
                            'warning.main'
                          }
                        >
                          {(match.score * 100).toFixed(0)}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={match.score * 100} 
                        sx={{ 
                          height: 8,
                          borderRadius: 1,
                          mt: 0.5,
                          mb: 0.5,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: 
                              match.score > 0.9 ? 'success.main' : 
                              match.score > 0.7 ? 'primary.main' : 
                              'warning.main'
                          }
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Right Column - Match Reasons & Actions */}
                  <Grid item xs={12} md={5}>
                    {/* Match Reasons */}
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Lý do trùng khớp:
                    </Typography>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 1.5, 
                        mb: 2,
                        maxHeight: 120, 
                        overflow: 'auto',
                        bgcolor: '#f9f9f9'
                      }}
                    >
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        {match.match_reasons.map((reason, i) => (
                          <li key={i}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                mb: 0.5,
                                color: reason.includes('chính xác') || reason.includes('exact') ? 'success.main' : 'text.primary'
                              }}
                            >
                              {reason}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    </Paper>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        startIcon={<SyncAltIcon />}
                        onClick={() => handleOpenConfirmDialog(match.transaction)}
                        disabled={mapping}
                        size="large"
                        sx={{ 
                          py: 1.2,
                          boxShadow: 2,
                          fontWeight: 'bold'
                        }}
                      >
                        {mapping ? (
                          <>
                            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                            Đang xử lý...
                          </>
                        ) : 'Ghép giao dịch này với đơn hàng'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          {/* Information footer */}
          <Card sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <InfoOutlinedIcon color="info" sx={{ mr: 1 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                  Lưu ý khi ghép giao dịch:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Khi ghép giao dịch, đơn hàng sẽ được chuyển sang trạng thái "Đã ghép giao dịch ngân hàng"
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Giao dịch sẽ được đánh dấu là đã xử lý và không thể ghép với đơn hàng khác
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Nên kiểm tra kỹ mô tả giao dịch và số tiền trước khi xác nhận ghép
                </Typography>
              </Box>
            </Box>
          </Card>
        </>
      )}
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="confirm-dialog-title" sx={{ bgcolor: 'primary.light', color: 'white' }}>
          Xác nhận ghép giao dịch
        </DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <>
              <DialogContentText id="confirm-dialog-description" sx={{ mt: 2 }}>
                Bạn có chắc chắn muốn ghép giao dịch ngân hàng này với đơn hàng #{order?.name} không?
              </DialogContentText>
              
              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Thông tin đơn hàng:
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Mã đơn hàng:</Typography>
                      <Typography variant="body1" fontWeight="medium">{order?.name}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Tổng tiền:</Typography>
                      <Typography variant="body1" fontWeight="bold" color="success.main">
                        {formatCurrency(order?.total_price)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
                
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Thông tin giao dịch:
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Mã giao dịch:</Typography>
                      <Typography variant="body1" fontWeight="medium">{selectedTransaction.code}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Số tiền:</Typography>
                      <Typography 
                        variant="body1" 
                        fontWeight="bold"
                        color={selectedTransaction.amount === order?.total_price ? 'success.main' : 'warning.main'}
                      >
                        {formatCurrency(selectedTransaction.amount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Mô tả:</Typography>
                      <Typography variant="body2">{selectedTransaction.description}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Thời gian:</Typography>
                      <Typography variant="body2">{formatDate(selectedTransaction.happened_at)}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
              
              {selectedTransaction.amount !== order?.total_price && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight="medium">
                    Cảnh báo: Số tiền giao dịch không khớp với tổng tiền đơn hàng!
                  </Typography>
                </Alert>
              )}
              
              <DialogContentText sx={{ mt: 3, color: 'text.primary' }}>
                <Typography variant="body2" fontWeight="medium" color="info.main">
                  Lưu ý: Sau khi ghép giao dịch, giao dịch này sẽ không thể được ghép với đơn hàng khác.
                </Typography>
              </DialogContentText>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={handleCloseConfirmDialog} 
            color="inherit"
            variant="outlined"
          >
            Hủy bỏ
          </Button>
          <Button 
            onClick={handleMapTransaction} 
            color="success" 
            variant="contained"
            autoFocus
            disabled={mapping}
            startIcon={mapping ? <CircularProgress size={20} /> : <CheckCircleOutlineIcon />}
          >
            {mapping ? 'Đang xử lý...' : 'Xác nhận ghép giao dịch'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrderBankTransactionMatch;

